import { Dropzone, getRejectedFilesList, notify, Space, Text } from '@billy/design-system'
import { useCallback } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useLocalStorage } from 'react-use'

import { UploadFilesErrorMessage } from '@features/attachments/elements/UploadFilesErrorMessage'

import { EmberRoute } from '../../../../enums/emberRoute'
import { LocalStorageKeys } from '../../../../enums/localStorageKeys'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useEmberRouteToUrlGenerator } from '../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useEmberTransitionTo } from '../../../../hooks/routing/useEmberTransitionTo'
import { APIError } from '../../../../utils'
import { useUploadBankStatement } from '../../hooks/useUploadBankStatement'
import { BankStatementFile } from '../../types/bankStatementFile'
import { BankUrlParams } from '../../types/bankUrlParams'

const ACCEPT_FILE_FORMATS = { 'text/csv': ['.csv'] }

export const BankStatementDropzone = () => {
  const { t } = useTranslation()
  const { bankAccountId } = useParams<BankUrlParams>()
  const [, setBankStatementUpload] = useLocalStorage(LocalStorageKeys.BankStatementUpload)
  const { transitionTo } = useEmberTransitionTo()
  const { generateUrl } = useEmberRouteToUrlGenerator()

  const { upload, isUploading } = useUploadBankStatement({
    onSuccess: (data: BankStatementFile) => {
      setBankStatementUpload(data)
      if (generateUrl) {
        const bankImportColumnsUrl = generateUrl(EmberRoute.BankImportColumns, bankAccountId)
        transitionTo(bankImportColumnsUrl)
      }
    },
    onError: (error: APIError) => {
      notify({
        id: NotificationKeys.BankStatementUpload,
        message: error.message,
        variant: 'error',
      })
    },
  })

  const handleDropAccepted = useCallback(
    ([file]: File[]) => {
      upload(file)
    },
    [upload],
  )

  const handleDropRejected = useCallback(
    (filesRejected: FileRejection[]) => {
      const uploadFilesErrors = getRejectedFilesList(filesRejected)
      notify({
        id: NotificationKeys.BankStatementUpload,
        message: UploadFilesErrorMessage({ uploadFilesErrors }),
        title: t('file.rejected.count', { count: filesRejected.length }),
        variant: 'warning',
      })
    },
    [t],
  )

  return (
    <>
      <Text alignment="center">{t('bankreconciliation.connect_bank.dropzone.title')}</Text>
      <Space size="s12" />
      <Dropzone
        accept={ACCEPT_FILE_FORMATS}
        contentText={t('bankreconciliation.connect_bank.dropzone.content')}
        footerText={t('bankreconciliation.connect_bank.dropzone.footer')}
        isUploading={isUploading}
        maxFiles={1}
        onDropAccepted={handleDropAccepted}
        onDropRejected={handleDropRejected}
      />
    </>
  )
}
